import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Loader2, RefreshCw, ChevronRight, Download, Search as SearchIcon, AlertTriangle, CheckCircle2, Clock, Zap, Trash2 } from "lucide-react";

interface LogRow {
  id: string;
  source_id: string | null;
  article_id: string | null;
  source_url: string | null;
  step: string;
  status: string;
  details: any;
  error_message: string | null;
  execution_id: string | null;
  trigger_type: string | null;
  duration_ms: number | null;
  items_fetched: number | null;
  items_stored: number | null;
  created_at: string;
}

interface SourceMini { id: string; topic: string; language: string }

interface ExecutionGroup {
  execution_id: string;
  source_id: string | null;
  trigger_type: string;
  started_at: string;
  status: string;
  duration_ms: number;
  items_fetched: number;
  items_stored: number;
  steps: LogRow[];
  error: string | null;
}

const STEP_ORDER = ["discovery", "scrape", "ai_rewrite", "insert", "complete", "run_summary", "pipeline"];

const statusBadge = (s: string) => {
  if (s === "success") return <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">Success</Badge>;
  if (s === "error" || s === "failed") return <Badge variant="destructive">Failed</Badge>;
  if (s === "partial") return <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30">Partial</Badge>;
  if (s === "fallback") return <Badge variant="secondary">Fallback</Badge>;
  return <Badge variant="outline">{s}</Badge>;
};

interface Props {
  sources: SourceMini[];
  onRetry: (sourceIds: string[]) => Promise<void>;
}

export default function SourceLogsPanel({ sources, onRetry }: Props) {
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterSource, setFilterSource] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTrigger, setFilterTrigger] = useState("all");
  const [filterRange, setFilterRange] = useState("24h");
  const [search, setSearch] = useState("");
  const [openExec, setOpenExec] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);

  const sourceMap = useMemo(() => Object.fromEntries(sources.map((s) => [s.id, s])), [sources]);

  // Memoise `load` so the useEffect below can list it as a dependency
  // without retriggering on every render — fixes the prior
  // /* eslint-disable-next-line */ exhaustive-deps suppression.
  const load = useCallback(async () => {
    setLoading(true);
    const since = new Date();
    if (filterRange === "1h") since.setHours(since.getHours() - 1);
    else if (filterRange === "24h") since.setHours(since.getHours() - 24);
    else if (filterRange === "7d") since.setDate(since.getDate() - 7);
    else if (filterRange === "30d") since.setDate(since.getDate() - 30);
    else since.setFullYear(since.getFullYear() - 5);

    let q = supabase
      .from("automation_logs")
      .select("*")
      .gte("created_at", since.toISOString())
      .order("created_at", { ascending: false })
      .limit(1000);
    if (filterSource !== "all") q = q.eq("source_id", filterSource);

    const { data, error } = await q;
    if (error) toast({ title: "Erreur de chargement", description: error.message, variant: "destructive" });
    setLogs((data as LogRow[]) || []);
    setLoading(false);
    // `toast` is a module-level import, not a hook value — listing it as a
    // dep is unnecessary per the react-hooks lint rule.
  }, [filterRange, filterSource]);

  useEffect(() => { void load(); }, [load]);

  // Group by execution_id (fallback: by source+minute)
  const executions: ExecutionGroup[] = useMemo(() => {
    const map = new Map<string, ExecutionGroup>();
    for (const l of logs) {
      const key = l.execution_id || `${l.source_id}-${l.created_at.slice(0, 16)}`;
      let g = map.get(key);
      if (!g) {
        g = {
          execution_id: l.execution_id || key,
          source_id: l.source_id,
          trigger_type: l.trigger_type || "auto",
          started_at: l.created_at,
          status: "success",
          duration_ms: 0,
          items_fetched: 0,
          items_stored: 0,
          steps: [],
          error: null,
        };
        map.set(key, g);
      }
      g.steps.push(l);
      if (new Date(l.created_at) < new Date(g.started_at)) g.started_at = l.created_at;
      if (l.step === "run_summary") {
        g.status = l.status;
        g.duration_ms = l.duration_ms || 0;
        g.items_fetched = l.items_fetched || 0;
        g.items_stored = l.items_stored || 0;
        if (l.error_message) g.error = l.error_message;
      }
      if ((l.status === "error" || l.status === "failed") && !g.error) g.error = l.error_message;
    }
    let arr = Array.from(map.values());
    // Apply UI filters
    if (filterStatus !== "all") arr = arr.filter((g) => g.status === filterStatus || (filterStatus === "failed" && g.steps.some((s) => s.status === "error")));
    if (filterTrigger !== "all") arr = arr.filter((g) => g.trigger_type === filterTrigger);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter((g) => {
        const topic = g.source_id ? sourceMap[g.source_id]?.topic?.toLowerCase() || "" : "";
        if (topic.includes(q)) return true;
        return g.steps.some(
          (s) =>
            s.error_message?.toLowerCase().includes(q) ||
            s.source_url?.toLowerCase().includes(q) ||
            JSON.stringify(s.details || {}).toLowerCase().includes(q)
        );
      });
    }
    arr.sort((a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime());
    return arr;
  }, [logs, filterStatus, filterTrigger, search, sourceMap]);

  const exportCSV = () => {
    const rows = [
      ["timestamp", "execution_id", "source", "trigger", "status", "items_fetched", "items_stored", "duration_ms", "error"],
      ...executions.map((g) => [
        g.started_at,
        g.execution_id,
        g.source_id ? sourceMap[g.source_id]?.topic || g.source_id : "",
        g.trigger_type,
        g.status,
        String(g.items_fetched),
        String(g.items_stored),
        String(g.duration_ms),
        (g.error || "").replace(/[\r\n,]/g, " "),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `source-logs-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(executions, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `source-logs-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRetry = async (sourceId: string) => {
    setRetrying(sourceId);
    try {
      await onRetry([sourceId]);
      await load();
    } finally {
      setRetrying(null);
    }
  };

  const clearOldLogs = async () => {
    if (!confirm("Supprimer tous les logs affichés ?")) return;
    const ids = logs.map((l) => l.id);
    if (!ids.length) return;
    const { error } = await supabase.from("automation_logs").delete().in("id", ids);
    if (error) toast({ title: "Erreur", description: error.message, variant: "destructive" });
    else {
      toast({ title: "Logs supprimés" });
      load();
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        <div className="relative flex-1 min-w-[200px]">
          <SearchIcon className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Rechercher (erreur, URL, mot-clé)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8"
          />
        </div>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="h-9 w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les sources</SelectItem>
            {sources.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.topic}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="success">Succès</SelectItem>
            <SelectItem value="partial">Partiel</SelectItem>
            <SelectItem value="failed">Échec</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterTrigger} onValueChange={setFilterTrigger}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Auto + Manuel</SelectItem>
            <SelectItem value="auto">Auto</SelectItem>
            <SelectItem value="manual">Manuel</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterRange} onValueChange={setFilterRange}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="1h">Dernière heure</SelectItem>
            <SelectItem value="24h">24 dernières heures</SelectItem>
            <SelectItem value="7d">7 jours</SelectItem>
            <SelectItem value="30d">30 jours</SelectItem>
            <SelectItem value="all">Tout</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Actualiser
        </Button>
        <Button variant="outline" size="sm" onClick={exportCSV}><Download className="h-3.5 w-3.5 mr-1" /> CSV</Button>
        <Button variant="outline" size="sm" onClick={exportJSON}><Download className="h-3.5 w-3.5 mr-1" /> JSON</Button>
        <Button variant="ghost" size="sm" onClick={clearOldLogs} className="text-destructive hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Vider
        </Button>
      </div>

      {/* Executions list */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Chargement des logs...
        </div>
      ) : executions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Aucune exécution dans cette période.
        </div>
      ) : (
        <div className="space-y-2">
          {executions.map((g) => {
            const topic = g.source_id ? sourceMap[g.source_id]?.topic || "Source supprimée" : "Multi-source";
            const sortedSteps = [...g.steps].sort((a, b) => {
              const oa = STEP_ORDER.indexOf(a.step);
              const ob = STEP_ORDER.indexOf(b.step);
              if (oa !== ob) return oa - ob;
              return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });
            const isOpen = openExec === g.execution_id;
            return (
              <Collapsible key={g.execution_id} open={isOpen} onOpenChange={(o) => setOpenExec(o ? g.execution_id : null)}>
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                  <CollapsibleTrigger className="w-full flex items-center gap-3 px-4 py-3 hover:bg-accent/30 transition-colors text-left">
                    <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    {g.status === "success" ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    ) : g.status === "partial" ? (
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm text-foreground truncate">{topic}</span>
                        {statusBadge(g.status)}
                        <Badge variant="outline" className="text-[10px]">
                          {g.trigger_type === "manual" ? <><Zap className="h-2.5 w-2.5 mr-1" /> Manuel</> : "Auto"}
                        </Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-3">
                        <span><Clock className="h-3 w-3 inline mr-0.5" /> {format(new Date(g.started_at), "dd/MM HH:mm:ss")}</span>
                        <span>{g.items_fetched} récupérés · {g.items_stored} stockés</span>
                        {g.duration_ms > 0 && <span>{(g.duration_ms / 1000).toFixed(1)}s</span>}
                        {g.error && <span className="text-destructive truncate max-w-md">⚠ {g.error}</span>}
                      </div>
                    </div>
                    {g.status !== "success" && g.source_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleRetry(g.source_id!); }}
                        disabled={retrying === g.source_id}
                      >
                        {retrying === g.source_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                        Réessayer
                      </Button>
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border-t border-border bg-muted/20 px-4 py-3 space-y-2">
                      {sortedSteps.map((s) => (
                        <div key={s.id} className="flex items-start gap-2 text-xs">
                          {s.status === "success" ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5" />
                          ) : s.status === "fallback" ? (
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mt-0.5" />
                          ) : (
                            <AlertTriangle className="h-3.5 w-3.5 text-destructive mt-0.5" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium text-foreground">{s.step}</span>
                              <span className="text-muted-foreground">{format(new Date(s.created_at), "HH:mm:ss")}</span>
                              {s.status !== "success" && <span className="text-[10px] uppercase text-muted-foreground">{s.status}</span>}
                            </div>
                            {s.source_url && <div className="text-muted-foreground truncate">{s.source_url}</div>}
                            {s.error_message && <div className="text-destructive">{s.error_message}</div>}
                            {s.details && Object.keys(s.details).length > 0 && (
                              <pre className="text-[10px] text-muted-foreground bg-background/50 rounded px-2 py-1 mt-1 overflow-x-auto">
{JSON.stringify(s.details, null, 0).slice(0, 200)}
                              </pre>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      )}
    </div>
  );
}
